import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Linkedin, Globe, Music, Heart, Code, Film, Building2, Sparkles } from 'lucide-react';
import MainMenu from '@components/ui/game/main-menu/MainMenu';
import { usePageBackground } from '@/hooks/usePageBackground';
import { getAssetPath } from '@utils/brandHelper';
import { useT } from '@/i18n';
import './Credits.css';

// Static data with translation keys for dynamic content
const TEAM_MEMBERS = [
  {
    name: 'José Carlos Tamayo',
    roleKey: 'credits.role_dev',
    icon: Code,
    link: 'https://www.linkedin.com/in/jose-carlos-tamayo/',
    linkIcon: Linkedin,
    linkLabel: 'LinkedIn',
  },
  {
    name: 'Christopher Vilca',
    roleKey: 'credits.role_av',
    icon: Film,
  },
];

const ORGANIZATIONS = [
  {
    name: 'Gamaru Studio',
    descKey: 'credits.org_gamaru',
    link: 'https://gamaru.studio',
    icon: Sparkles,
  },
  {
    name: 'Almaquinta',
    descKey: 'credits.org_almaquinta',
    link: 'https://almaquinta.com',
    icon: Building2,
  },
];

const SPECIAL_THANKS = [
  {
    name: 'Matthew Pablo',
    contribKey: 'credits.thanks_music',
    link: 'https://opengameart.org/users/matthew-pablo',
    icon: Music,
  },
  {
    name: 'Lucide',
    contribKey: 'credits.thanks_icons',
    link: 'https://lucide.dev',
    icon: Sparkles,
  },
];

export default function Credits() {
  const navigate = useNavigate();
  const t = useT();

  // Set credits background via Asset system
  usePageBackground('bg.credits');

  return (
    <div className="main-container main-container--credits">
      <MainMenu />

      <div className="credits-container style-glass style-primary">
        <div className="credits-header">
          <img
            src={getAssetPath('assets/logosimple.webp')}
            alt="Logo"
            className="credits-logo"
          />
          <h1>{t('credits.title')}</h1>
        </div>

        {/* Team Section */}
        <section className="credits-section">
          <h2 className="credits-section-title">
            <Code size={24} />
            <span>{t('credits.team')}</span>
          </h2>
          <div className="credits-cards">
            {TEAM_MEMBERS.map((member) => (
              <div key={member.name} className="credit-card credit-card--team">
                <div className="credit-card-icon">
                  <member.icon size={32} />
                </div>
                <div className="credit-card-content">
                  <h3>{member.name}</h3>
                  <p className="credit-role">{t(member.roleKey)}</p>
                </div>
                {member.link && (
                  <a
                    href={member.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="credit-link"
                  >
                    <member.linkIcon size={18} />
                    <span>{member.linkLabel}</span>
                  </a>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Organizations Section */}
        <section className="credits-section">
          <h2 className="credits-section-title">
            <Building2 size={24} />
            <span>{t('credits.organizations')}</span>
          </h2>
          <div className="credits-cards">
            {ORGANIZATIONS.map((org) => (
              <div key={org.name} className="credit-card credit-card--org">
                <div className="credit-card-icon">
                  <org.icon size={32} />
                </div>
                <div className="credit-card-content">
                  <h3>{org.name}</h3>
                  <p className="credit-description">{t(org.descKey)}</p>
                </div>
                <a
                  href={org.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="credit-link"
                >
                  <Globe size={18} />
                  <span>{t('credits.visit')}</span>
                </a>
              </div>
            ))}
          </div>
        </section>

        {/* Special Thanks Section */}
        <section className="credits-section">
          <h2 className="credits-section-title">
            <Heart size={24} />
            <span>{t('credits.special_thanks')}</span>
          </h2>
          <div className="credits-cards credits-cards--thanks">
            {SPECIAL_THANKS.map((thanks) => (
              <div key={thanks.name} className="credit-card credit-card--thanks">
                <div className="credit-card-icon">
                  <thanks.icon size={28} />
                </div>
                <div className="credit-card-content">
                  <h3>{thanks.name}</h3>
                  <p className="credit-contribution">{t(thanks.contribKey)}</p>
                </div>
                <a
                  href={thanks.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="credit-link"
                >
                  <Globe size={18} />
                  <span>{t('credits.view')}</span>
                </a>
              </div>
            ))}
          </div>
        </section>

        {/* Footer */}
        <div className="credits-footer">
          <p>{t('credits.made_with')} <Heart size={16} className="heart-icon" /> {t('credits.in_peru')}</p>
          <button className="button" onClick={() => navigate(-1)}>
            {t('common.back')}
          </button>
        </div>
      </div>
    </div>
  );
}
